import {useAuthClientId, useAuthTeamId, useAuthClient} from 'components/organisms/Providers/AuthProvider/AuthProvider';
import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react'
import {useTable} from 'components/organisms/Providers/TableProvider/TableProvider';
import constants from 'helpers/constants';
import utils from 'helpers/utils';
import EntitiesTableProvider from 'components/organisms/Providers/TableProvider/EntitiesTableProvider';
import {useEffectEvent, useEffectItem, useProviderView} from 'helpers/hooks/utils';
import {useEntityList, useEntityStore} from 'services/entity/entity.hooks';
import {
  getEntityCsi,
  useDealflowEntityColumns,
  useDealflowEntityFilterGroups, useDealflowEntityGraphs,
  useDealflowEntityPanels
} from 'services/entity/entity.utils';
import {useClientGet} from 'services/client/client.hooks';
import {useViewList} from 'services/view/view.hooks';
import {useServiceInvalidateQuery} from 'stores/hooks/service';

const DealflowEntitiesTableLoader = ({children, active}) => {
  const store = useEntityStore();
  const tableProvider = useTable();
  const listState = useEffectItem(tableProvider.appliedListState());

  const teamId = useAuthTeamId();
  const queryKeyRef = useRef(null);
  const invalidateQuery = useServiceInvalidateQuery();
  const handleMonitor = (prev, next) => {
    if (queryKeyRef.current && utils.isDefined(tableProvider.list?.data)) {
      if (utils.isDefined(prev) && !(prev?.preload || prev?.dummy) && !(next?.preload || next?.dummy)) {
        const prevStatusId = getEntityCsi(prev?.data, teamId)?.status;
        const nextStatusId = getEntityCsi(next?.data, teamId)?.status;

        if ((+prevStatusId > 0 && !(+nextStatusId > 0)) || (+nextStatusId > 0 && !(+prevStatusId > 0))) {
          invalidateQuery(store, [{
            hash: 'dummy',
            keys: queryKeyRef.current.slice(1, 3),
            exact: false, refetch: true, remove: false
          }], true);
        }
      }
    }
  };

  const list = useEntityList({
    clientId: tableProvider.context?.data?.clientId,
    search: listState.search,
    filter: listState.filter,
    sort: listState.sort,
    page: listState.pagination.pageIndex,
    pageSize: listState.pagination.pageSize
  }, {
    ...constants.queryOptions.infinite,
    enablePreload: true,
    enabled: (active && listState.active) && tableProvider.context?.data?.clientId >= 0,
    monitorCallback: handleMonitor
  });

  const setListEvent = useEffectEvent(tableProvider.setList);
  useLayoutEffect(() => {
    queryKeyRef.current = list.query.queryKey;
    setListEvent?.(list);
  }, [setListEvent, list]);

  return <React.Fragment>
    {children}
  </React.Fragment>
}

const DealflowEntitiesTableTestLoader = ({children, active}) => {
  const store = useEntityStore();
  const tableProvider = useTable();
  const listState = useEffectItem(tableProvider.appliedListState('test'));

  const teamId = useAuthTeamId();
  const queryKeyRef = useRef(null);
  const invalidateQuery = useServiceInvalidateQuery();
  const handleMonitor = (prev, next) => {
    if (queryKeyRef.current && utils.isDefined(tableProvider.testList?.data)) {
      if (utils.isDefined(prev) && !(prev?.preload || prev?.dummy) && !(next?.preload || next?.dummy)) {
        const prevStatusId = getEntityCsi(prev?.data, teamId)?.status;
        const nextStatusId = getEntityCsi(next?.data, teamId)?.status;

        if ((+prevStatusId > 0 && !(+nextStatusId > 0)) || (+nextStatusId > 0 && !(+prevStatusId > 0))) {
          invalidateQuery(store, [{
            hash: 'dummy',
            keys: queryKeyRef.current.slice(1, 3),
            exact: false, refetch: true, remove: false
          }], true);
        }
      }
    }
  };

  const list = useEntityList({
    clientId: tableProvider.context?.data?.clientId,
    search: listState.search,
    filter: listState.filter,
    sort: listState.sort,
    page: listState.pagination.pageIndex,
    pageSize: listState.pagination.pageSize
  }, {
    ...constants.queryOptions.runOnce,
    enabled: (active && listState.active) && tableProvider.context?.data?.clientId >= 0,
    monitorCallback: handleMonitor
  });

  const setTestListEvent = useEffectEvent(tableProvider.setTestList);
  useLayoutEffect(() => {
    queryKeyRef.current = list.query.queryKey;
    setTestListEvent?.(list);
  }, [setTestListEvent, list]);

  return <React.Fragment>
    {children}
  </React.Fragment>
}

const DealflowEntitiesTableKanbanListLoader = ({children, options, active}) => {
  const store = useEntityStore();
  const tableProvider = useTable();
  const listState = useEffectItem(tableProvider.appliedListState('kanban'));
  const kanbanState = useEffectItem(tableProvider.kanbanState);

  const queryKeyRef = useRef(null);
  const enabled = active && tableProvider.context?.data?.clientId >= 0 &&
    (kanbanState?.panelVisibility?.[options?.panelId] ?? true) &&
    !(tableProvider.list?.status?.isLoading || !tableProvider.list?.status?.isSuccess);

  // these kanban lists are temporary and can not refreshed
  // by the service because they are not connected to anything
  const teamId = useAuthTeamId();
  const invalidateQuery = useServiceInvalidateQuery();
  const handleMonitor = (prev, next) => {
    if (queryKeyRef.current && utils.isDefined(tableProvider.kanbanLists[options.id]?.data)) {
      if (utils.isDefined(prev) && !(prev?.preload || prev?.dummy) && !(next?.preload || next?.dummy)) {
        const prevStatusId = getEntityCsi(prev?.data, teamId)?.status;
        const nextStatusId = getEntityCsi(next?.data, teamId)?.status;

        if ((+prevStatusId === +options?.id || +nextStatusId === +options?.id) && +prevStatusId !== +nextStatusId) {
          invalidateQuery(store, [{
            hash: 'dummy',
            keys: queryKeyRef.current.slice(1, 3),
            exact: false, refetch: true, remove: false
          }], true);
        }
      }
    }
  };

  const filter = useMemo(() => {
    const statusFilter = (listState.filter ?? []).find((f) => f.id === 'status');
    const filteredOut = !statusFilter ? false : (
      utils.toArray(statusFilter.value).find((s) => s.toString() !== '-0') && (
        (
          utils.toArray(statusFilter.value).find((s) => !s.toString().startsWith('-')) &&
          !utils.toArray(statusFilter.value).find((s) => s.toString() === `${options.id}`)
        ) ||
        utils.toArray(statusFilter.value).find((s) => s.toString() === `-${options.id}`)
      )
    )

    if (filteredOut) {
      return [{id: 'status', value: `${options.id}`}, {id: 'status', value: `-${options.id}`}];
    } else {
      return utils.applyFilters(listState.filter, [{id: 'status', value: +options.id}], ['status']);
    }
  }, [options.id, listState.filter]);

  const sort = useMemo(() => {
    return utils.addSort(null, {id: 'dealflow_changed', desc: true});
  }, []);

  const list = useEntityList({
    clientId: tableProvider.context?.data?.clientId,
    search: listState.search,
    filter: filter,
    sort: sort,
    page: listState.pagination.pageIndex,
    pageSize: listState.pagination.pageSize,
    cards: true
  }, {
    ...constants.queryOptions.infinite,
    enablePreload: true,
    lookupKey: `dealflowEntitiesKanban_${options?.id}`,
    enabled: enabled && tableProvider.context?.data?.clientId >= 0,
    monitorCallback: handleMonitor
  });

  const setKanbanListEvent = useEffectEvent(tableProvider.setKanbanList);
  useLayoutEffect(() => {
    queryKeyRef.current = list.query.queryKey;
    setKanbanListEvent?.(options?.id, list);
  }, [setKanbanListEvent, options?.id, list]);

  return <React.Fragment>
    {children}
  </React.Fragment>
}

const DealflowEntitiesTableKanbanLoader = ({children, active}) => {
  const [initialised, setInitialised] = useState(false);

  const tableProvider = useTable();
  const client = useAuthClient();
  const groups = client?.dealflowGroups;

  const lists = useMemo(() => {
    if (groups) {
      return groups?.reduce((a, group) => {
        return a.concat(group.statuses
          .filter((status) => +status.statusId > 0)
          .map((status) => ({
            id: +status.statusId,
            panelId: +group.groupId
          })));
      }, [])
    } else {
      return null;
    }
  }, [groups]);

  // load the kanban lists
  const listState = useEffectItem(tableProvider.listState);
  const setSearchEvent = useEffectEvent(tableProvider?.kanbanListState?.setSearch);
  const setFilterEvent = useEffectEvent(tableProvider?.kanbanListState?.setFilter);
  const setCustomEvent = useEffectEvent(tableProvider?.kanbanListState?.setCustom);
  useEffect(() => {
    setSearchEvent?.(listState.search);
    setFilterEvent?.(listState.filter);
    setCustomEvent?.(listState.custom);
    setInitialised(true);
  }, [listState.search, listState.filter, listState.custom, setSearchEvent, setFilterEvent, setCustomEvent]);

  return <React.Fragment>
    {lists?.map((list) => {
      return <DealflowEntitiesTableKanbanListLoader key={list.id}
                                                    options={list}
                                                    active={initialised && active}/>
    })}
    {children}
  </React.Fragment>
}

const DealflowEntitiesTableProvider = (props) => {
  const clientId = useAuthClientId();
  const teamId = useAuthTeamId();

  const client = useClientGet({clientId}, {enabled: clientId >= 0});

  const views = useViewList({
    relationType: 'collection',
    relationId: client?.data?.universeCollectionId,
    subscribed: true,
    allPages: true
  }, {
    enabled: client?.data?.universeCollectionId > 0
  });
  const view = useProviderView('dealflow.entity', views?.data, props.skipView);

  const columns = useDealflowEntityColumns(view);
  const panels = useDealflowEntityPanels(view);
  const filterGroups = useDealflowEntityFilterGroups(view);
  const graphs = useDealflowEntityGraphs(view);

  const isLoading = props?.isLoading || client.status?.isLoading || views.status?.isLoading;
  const isDefinitionsLoading = props?.isDefinitionsLoading;

  const columnDefinitions = useMemo(() => {
    return columns?.length > 0 ? columns : null;
  }, [columns]);

  const filterGroupDefinitions = useMemo(() => {
    return filterGroups?.length > 0 ? filterGroups : null;
  }, [filterGroups]);

  const panelDefinitions = useMemo(() => {
    return panels?.length > 0 ? panels : null;
  }, [panels]);

  const graphDefinitions = useMemo(() => {
    return graphs?.length > 0 ? graphs : null;
  }, [graphs]);

  const options = useMemo(() => ({
    listState: {
      initial: {
        pageSize: 25
      },
      options: {
        type: constants.appState.type.local,
        name: `dealflowEntities_${teamId}`,
        searchParams: true
      }
    },
    testListState: {
      initial: {
        active: false,
        pageSize: 0
      },
      options: {
        name: `dealflowEntities_${teamId}_test`
      }
    },
    kanbanListState: {
      initial: {
        pageSize: 10
      },
      options: {
        name: `dealflowEntities_${teamId}_kanban`
      }
    },
    listSelection: {
      options: {
        max: constants.selection.max.dealflow
      }
    },
    columnState: {
      initial: {},
      options: {
        name: `dealflowEntities_${teamId}`,
        type: constants.appState.type.local
      }
    },
    kanbanState: {
      initial: {},
      options: {
        name: `dealflowEntities_${teamId}`,
        type: constants.appState.type.local
      }
    },
    graphState: {
      initial: {},
      options: {
        name: `dealflowEntities_${teamId}`,
        type: constants.appState.type.local
      }
    },
    assistantState: {
      initial: {},
      options: {
        name: `dealflowEntities_${teamId}`,
        type: constants.appState.type.local
      }
    },
    columnDefinitions,
    panelDefinitions,
    filterGroupDefinitions,
    graphDefinitions
  }), [columnDefinitions, panelDefinitions, graphDefinitions, filterGroupDefinitions, teamId]);

  const fieldData = useMemo(() => {
    return {};
  }, []);

  const updaters = useMemo(() => {
    return {};
  }, []);

  const defaults = useMemo(() => {
    return utils.mergeObjects({
      list: {
        filter: [{id: 'status', value: '-0'}],
        sort: [{id: 'dealflow_added', desc: true}]
      }
    }, props.defaults);
  }, [props.defaults]);

  return <EntitiesTableProvider {...props}
                                view={view}
                                options={options}
                                fieldData={fieldData}
                                context={client}
                                updaters={updaters}
                                defaults={defaults}
                                views={views}
                                LoaderComponent={DealflowEntitiesTableLoader}
                                TestLoaderComponent={DealflowEntitiesTableTestLoader}
                                KanbanLoaderComponent={DealflowEntitiesTableKanbanLoader}
                                isDefinitionsLoading={isDefinitionsLoading}
                                isLoading={isLoading}>
    {props.children}
  </EntitiesTableProvider>
};

DealflowEntitiesTableProvider.propTypes = {
}

export default DealflowEntitiesTableProvider;
